import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  alpha,
  List,
  ListItem
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import { apiClient } from '../../services/api';

interface ProjectLogDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
}


interface FileUploadItem {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  extractedContent?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`log-tabpanel-${index}`}
      aria-labelledby={`log-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default function ProjectLogDialog({ open, onClose, projectId }: ProjectLogDialogProps) {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [logContent, setLogContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingLatest, setIsLoadingLatest] = useState(false);

  // File upload states
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // Supported file types for logs
  const ACCEPTED_LOG_TYPES = {
    'application/pdf': { icon: DescriptionIcon, label: 'PDF' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: DescriptionIcon, label: 'DOCX' },
    'text/plain': { icon: DescriptionIcon, label: 'TXT' },
    'text/markdown': { icon: DescriptionIcon, label: 'MD' },
    'image/png': { icon: ImageIcon, label: 'PNG' },
    'image/jpeg': { icon: ImageIcon, label: 'JPG' },
  };

  const FILE_EXTENSIONS = Object.keys(ACCEPTED_LOG_TYPES).flatMap(type => {
    const extension = type.split('/')[1];
    switch (type) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return ['.docx'];
      default:
        return [`.${extension}`];
    }
  }).join(',');

  // 回显最近一次日志（只查询 writing 类型）
  useEffect(() => {
    if (!open || !projectId) return;
    const fetchLatest = async () => {
      setIsLoadingLatest(true);
      setError(null);
      try {
        // 使用新的 facts API，查询 log_type='writing' 的最新日志
        const response = await apiClient.get(`/projects/${projectId}/facts/latest`);
        if (response.success && response.data) {
          setLogContent(response.data.log?.content || '');
        }
      } catch (err) {
        // 忽略回显错误
      } finally {
        setIsLoadingLatest(false);
      }
    };
    fetchLatest();
  }, [open, projectId]);

  // File upload handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file =>
      Object.keys(ACCEPTED_LOG_TYPES).includes(file.type) ||
      Object.values(ACCEPTED_LOG_TYPES).some(({ label }) =>
        file.name.toLowerCase().endsWith(label.toLowerCase())
      )
    );

    if (validFiles.length > 0) {
      addFiles(validFiles);
    } else {
      setError('不支持的文件格式。支持格式：PDF, DOCX, TXT, MD, PNG, JPG');
    }
  }, []);

  const addFiles = (newFiles: File[]) => {
    const fileItems: FileUploadItem[] = newFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));

    setFiles(prev => [...prev, ...fileItems]);
    setError('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const extractTextFromFile = async (file: File): Promise<string> => {
    // For text files, read directly
    if (file.type === 'text/plain' || file.type === 'text/markdown') {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });
    }

    // For other files, use the backend document processing
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/projects/${projectId}/documents/upload-to-uploaddir`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success && response.data?.processed_content) {
        return response.data.processed_content;
      } else {
        throw new Error('无法提取文件内容');
      }
    } catch (error) {
      throw error;
    }
  };

  const uploadFile = async (fileItem: FileUploadItem): Promise<void> => {
    const { file } = fileItem;

    try {
      setFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, status: 'uploading' as const }
          : item
      ));

      // Extract text content from file
      const extractedContent = await extractTextFromFile(file);

      setFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, extractedContent, status: 'success' as const }
          : item
      ));

      // Append extracted content to log content
      setLogContent(prev => {
        const newContent = prev + (prev ? '\n\n' : '') +
          `=== 来自文件 "${file.name}" 的内容 ===\n${extractedContent}`;
        return newContent;
      });

    } catch (error: any) {
      setFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, status: 'error' as const, error: error.message || '处理失败' }
          : item
      ));
    }
  };

  const handleFileUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setError('');

    // Process files sequentially
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status === 'pending') {
        await uploadFile(fileItem);
      }
    }

    setIsUploading(false);

    // Switch to text tab after processing
    setTabValue(0);
  };

  const handleSave = async () => {
    if (!logContent.trim()) {
      setError('请输入日志内容');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      // 直接保存日志（使用新的 facts API，保存时设置 log_type='writing'）
      const saveResponse = await apiClient.post(`/projects/${projectId}/facts/save`, {
        content: logContent,
        log_type: 'writing'  // 保存时设置类型为 writing
      });

      if (!saveResponse.success) {
        throw new Error(saveResponse.error || '保存失败');
      }

      onClose();
    } catch (err: any) {
      setError(err.message || '保存失败');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {}} // 禁用点击空白区域关闭
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown // 禁用ESC键关闭
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">项目日志分析</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tabs for input method selection */}
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="文本输入" />
            <Tab label="文件上传" />
          </Tabs>

          {/* Text Input Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography variant="subtitle2" gutterBottom>
              请输入项目日志内容：
            </Typography>
            <TextField
              multiline
              rows={10}
              fullWidth
              value={logContent}
              onChange={(e) => setLogContent(e.target.value)}
              placeholder="在此粘贴项目日志内容..."
              variant="outlined"
              disabled={isLoadingLatest || isSaving || isUploading}
            />
          </TabPanel>

          {/* File Upload Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="subtitle2" gutterBottom>
              上传日志文件（支持PDF、DOCX、TXT、MD、PNG、JPG格式）：
            </Typography>

            {/* Drag and drop area */}
            <Box
              sx={{
                border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: isDragging
                  ? alpha(theme.palette.primary.main, 0.05)
                  : alpha(theme.palette.action.hover, 0.02),
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                mb: 3,
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                  borderColor: theme.palette.primary.main
                }
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={FILE_EXTENSIONS}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              <CloudUploadIcon sx={{
                fontSize: 48,
                color: isDragging ? 'primary.main' : 'action.disabled',
                mb: 2
              }} />

              <Typography variant="h6" gutterBottom>
                {isDragging ? '松开以上传文件' : '拖拽文件到此处或点击选择'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                支持格式：PDF, DOCX, TXT, MD, PNG, JPG
              </Typography>
            </Box>

            {/* File list */}
            {files.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  待处理文件 ({files.length})
                </Typography>

                <List dense>
                  {files.map((fileItem, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <InsertDriveFileIcon />
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 200
                          }}
                        >
                          {fileItem.file.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={fileItem.file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {fileItem.status === 'uploading' && (
                          <CircularProgress size={20} />
                        )}

                        {fileItem.status === 'success' && (
                          <Typography variant="caption" color="success.main">
                            ✓ 已处理
                          </Typography>
                        )}

                        {fileItem.status === 'error' && (
                          <Typography variant="caption" color="error.main">
                            ✗ {fileItem.error}
                          </Typography>
                        )}

                        <IconButton
                          onClick={() => removeFile(index)}
                          disabled={isUploading || fileItem.status === 'uploading'}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItem>
                  ))}
                </List>

                {/* Upload button */}
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={handleFileUpload}
                    variant="contained"
                    disabled={isUploading || files.length === 0 || files.every(f => f.status !== 'pending')}
                    startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  >
                    {isUploading ? '处理中...' : `处理文件 (${files.filter(f => f.status === 'pending').length})`}
                  </Button>
                </Box>
              </Box>
            )}
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          关闭
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || !logContent.trim()}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
